import type React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Alert,
    Card,
    Chip,
    Table,
    Typography,
    type TypographyProps,
} from '@heroui/react';
import { countParam } from '@/pages/catalogImport/notices';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { LabelListDownloadButton } from './LabelListDownloadButton';
import type { LabelsCardData } from './labelsCardMeta';
import type { LabelListDownload } from './useLabelListDownload';

type LabelsCardViewProps = {
    labels: LabelsCardData;
    download: LabelListDownload;
};

// The card's title is a second-level heading under the step's title.
const asH2: NonNullable<TypographyProps['render']> = ({
    children,
    ...domProps
}) => <h2 {...domProps}>{children}</h2>;

const COLUMNS = ['product', 'variant', 'sku', 'barcode', 'labels'] as const;

const COPY = 'catalogImport.result.labels';

/**
 * **Print labels for N new barcodes** (board `bc-Result`): a "Before you
 * sell" chip with the count and units, why the labels are needed, a table of
 * the first generated barcodes (product, variant, SKU, new barcode, labels)
 * with "N of M shown" when there are more, and **Download label list
 * (.xlsx)** with where to find the list again. A missing variant or SKU is an
 * empty cell. A failed download shows under the button.
 */
export const LabelsCardView: React.FC<LabelsCardViewProps> = ({
    labels,
    download,
}) => {
    const { t } = useTranslation('pages');
    const { t: tc } = useTranslation('components');
    const { shown, total, units } = labels;

    return (
        <Card
            className="gap-4 p-4 sm:p-6"
            {...getNodeTestIdProp('catalog-import-result-labels')}
        >
            <Card.Header className="gap-1">
                <div className="flex flex-wrap items-center gap-2">
                    <Chip color="warning" size="sm" variant="soft">
                        {t(`${COPY}.chip`)}
                    </Chip>
                    <Typography
                        color="muted"
                        type="body-xs"
                        {...getNodeTestIdProp(
                            'catalog-import-result-labels-count',
                        )}
                    >
                        {t(`${COPY}.count`, {
                            barcodes: t(`${COPY}.barcodes`, {
                                count: total,
                                barcodes: countParam(total),
                            }),
                            units: t(`${COPY}.units`, {
                                count: units,
                                units: countParam(units),
                            }),
                        })}
                    </Typography>
                </div>
                <Card.Title render={asH2}>
                    {t(`${COPY}.title`, {
                        count: total,
                        barcodes: countParam(total),
                    })}
                </Card.Title>
                <Card.Description>{t(`${COPY}.description`)}</Card.Description>
            </Card.Header>

            <Card.Content>
                <Table variant="secondary">
                    <Table.ScrollContainer>
                        <Table.Content
                            aria-label={t(`${COPY}.tableAria`)}
                            className="min-w-[760px] text-sm"
                        >
                            <Table.Header>
                                {COLUMNS.map((column) => (
                                    <Table.Column
                                        className={
                                            column === 'labels'
                                                ? 'text-end'
                                                : undefined
                                        }
                                        id={column}
                                        isRowHeader={column === 'product'}
                                        key={column}
                                    >
                                        {t(`${COPY}.columns.${column}`)}
                                    </Table.Column>
                                ))}
                            </Table.Header>
                            <Table.Body>
                                {shown.map((row) => (
                                    <Table.Row
                                        id={row.variantId}
                                        key={row.variantId}
                                        {...getNodeTestIdProp(
                                            `catalog-import-result-labels-row-${row.variantId}`,
                                        )}
                                    >
                                        <Table.Cell className="font-medium">
                                            {row.productName}
                                        </Table.Cell>
                                        <Table.Cell>
                                            {row.variantLabel ?? ''}
                                        </Table.Cell>
                                        <Table.Cell className="font-mono">
                                            {row.sku ?? ''}
                                        </Table.Cell>
                                        <Table.Cell className="font-mono tabular-nums">
                                            {row.barcode}
                                        </Table.Cell>
                                        <Table.Cell className="text-end tabular-nums">
                                            {countParam(row.units)}
                                        </Table.Cell>
                                    </Table.Row>
                                ))}
                            </Table.Body>
                        </Table.Content>
                    </Table.ScrollContainer>
                    {shown.length < total ? (
                        <Table.Footer>
                            <Typography
                                color="muted"
                                type="body-sm"
                                {...getNodeTestIdProp(
                                    'catalog-import-result-labels-shown',
                                )}
                            >
                                {t(`${COPY}.shown`, {
                                    shown: countParam(shown.length),
                                    total: countParam(total),
                                })}
                            </Typography>
                        </Table.Footer>
                    ) : null}
                </Table>
            </Card.Content>

            <Card.Footer className="flex flex-col items-stretch gap-3">
                <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                    <LabelListDownloadButton
                        className="w-full sm:w-auto"
                        download={download}
                        testId="catalog-import-result-labels-download"
                        withIcon
                    />
                    <Typography color="muted" type="body-xs">
                        {t(`${COPY}.again`)}
                    </Typography>
                </div>
                {download.downloadFailed ? (
                    <Alert
                        role="alert"
                        status="danger"
                        {...getNodeTestIdProp(
                            'catalog-import-result-labels-failed',
                        )}
                    >
                        <Alert.Indicator />
                        <Alert.Content>
                            <Alert.Description>
                                {tc('labelList.downloadFailed')}
                            </Alert.Description>
                        </Alert.Content>
                    </Alert>
                ) : null}
            </Card.Footer>
        </Card>
    );
};
